import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Clock, MapPin, Phone } from 'lucide-react';

const BlogPage = () => {
  const blogPosts = [
    {
      id: 1,
      slug: 'best-corporate-awards-san-diego',
      title: 'Top 10 Corporate Award Ideas for San Diego Businesses in 2026',
      excerpt: 'Discover the best employee recognition awards, crystal trophies, and custom plaques that San Diego companies are using to boost morale and retain top talent.',
      image: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/qafcxzfa_unnamed.webp',
      date: 'June 28, 2026',
      author: 'RC Awards Team',
      readTime: '5 min read',
      category: 'Corporate Awards',
      content: `
        <p>Employee recognition is more important than ever for <strong>San Diego businesses</strong> looking to retain top talent and build a positive company culture. At <strong>RC Awards & Promotions</strong>, we've been helping local companies celebrate their teams for over 30 years. Here are the top corporate award ideas trending in 2026:</p>

        <h3>1. Crystal Awards for Executive Recognition</h3>
        <p>Nothing says "elite achievement" like a beautifully crafted <strong>crystal award</strong>. Perfect for CEO awards, sales milestones, and retirement gifts. Our <a href="/products">crystal award collection</a> features hand-cut designs that catch the light and make a stunning impression on any executive's desk.</p>

        <h3>2. Custom Engraved Plaques</h3>
        <p>Traditional yet timeless, <strong>custom plaques</strong> remain the go-to choice for employee of the month programs, years of service awards, and departmental recognition. Our <a href="/services">custom engraving services</a> can personalize any plaque with names, dates, and company logos.</p>

        <h3>3. Achievement Trophies for Sales Teams</h3>
        <p>Motivate your sales force with stunning <strong>trophies</strong> that celebrate quarterly wins and annual achievements. From classic column trophies to modern sculptural designs, we have options for every budget.</p>

        <h3>4. Personalized Desk Awards</h3>
        <p>Compact desk awards make daily reminders of achievement. Popular among <strong>San Diego tech companies</strong>, <strong>La Mesa startups</strong>, and <strong>downtown San Diego corporations</strong>.</p>

        <h3>5. Team Achievement Plaques</h3>
        <p>Celebrate entire departments with large display plaques that can hang in common areas. Perfect for project completion milestones and team records.</p>

        <h3>Why San Diego Companies Choose RC Awards</h3>
        <p>Located on <strong>El Cajon Boulevard</strong>, we serve businesses throughout <strong>San Diego County</strong> including <strong>Chula Vista</strong>, <strong>National City</strong>, <strong>El Cajon</strong>, <strong>La Mesa</strong>, and <strong>Mission Valley</strong>. With our <strong>4-day turnaround</strong> and <a href="/services">rush services</a>, we can meet your tightest deadlines.</p>

        <p><strong>Ready to recognize your team?</strong> <a href="/contact">Contact us today</a> for a free quote or call <a href="tel:6196401633">(619) 640-1633</a>.</p>
      `
    },
    {
      id: 2,
      slug: 'youth-sports-trophies-san-diego',
      title: 'Complete Guide to Youth Sports Trophies & Medals in San Diego',
      excerpt: 'Everything San Diego youth leagues, schools, and coaches need to know about ordering trophies, medals, and ribbons for young athletes.',
      image: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/oumok1jw_unnamed%20%286%29.webp',
      date: 'June 25, 2026',
      author: 'RC Awards Team',
      readTime: '6 min read',
      category: 'Sports Awards',
      content: `
        <p>Youth sports are a cornerstone of <strong>San Diego community life</strong>. From <strong>Little League baseball</strong> in <strong>Clairemont</strong> to <strong>AYSO soccer</strong> in <strong>Scripps Ranch</strong>, young athletes deserve recognition for their hard work and sportsmanship. Here's your complete guide to ordering <strong>youth sports trophies</strong> in San Diego.</p>

        <h3>Choosing the Right Trophy Size</h3>
        <p>For <strong>youth sports awards</strong>, we recommend:</p>
        <ul>
          <li><strong>Participation trophies:</strong> 6-8 inches - perfect for all team members</li>
          <li><strong>MVP/Top scorer:</strong> 10-12 inches - stands out from participation awards</li>
          <li><strong>Championship trophies:</strong> 14-18 inches - the centerpiece of any trophy case</li>
        </ul>

        <h3>Popular Sports We Serve</h3>
        <p>Our <a href="/products">trophy collection</a> covers every youth sport in <strong>San Diego County</strong>:</p>
        <ul>
          <li><strong>Soccer trophies</strong> - AYSO, club teams, school leagues</li>
          <li><strong>Baseball & softball trophies</strong> - Little League, Pony League, travel ball</li>
          <li><strong>Basketball trophies</strong> - CYO, rec leagues, AAU</li>
          <li><strong>Swimming medals</strong> - for swim meets and championships</li>
          <li><strong>Football trophies</strong> - Pop Warner, flag football</li>
          <li><strong>Martial arts awards</strong> - belt ceremonies, tournaments</li>
        </ul>

        <h3>Medals vs. Trophies: What's Best?</h3>
        <p><strong>Medals</strong> are ideal for large tournaments where many participants need recognition. They're cost-effective and athletes love wearing them. <strong>Trophies</strong> are better for season-end awards, MVPs, and championships where you want a lasting keepsake.</p>

        <h3>Bulk Ordering for Leagues</h3>
        <p>Running a youth league in <strong>El Cajon</strong>, <strong>Santee</strong>, or <strong>Poway</strong>? We offer significant discounts on bulk orders of 50+ trophies. Our team can help you design a consistent look across all age divisions.</p>

        <h3>Custom Engraving for Teams</h3>
        <p>Every trophy can be personalized with:</p>
        <ul>
          <li>Player name</li>
          <li>Team name and year</li>
          <li>League logo</li>
          <li>Achievement (MVP, Most Improved, Sportsmanship)</li>
        </ul>
        <p>Learn more about our <a href="/services">custom engraving services</a>.</p>

        <h3>Fast Turnaround for End-of-Season</h3>
        <p>We know coaches often wait until the last minute! Our standard <strong>4-day turnaround</strong> gets your awards ready fast. Need them sooner? Our <a href="/services">rush service</a> can deliver in as little as 24-48 hours.</p>

        <h3>Visit Our San Diego Trophy Shop</h3>
        <p>Stop by our showroom at <strong>2509 El Cajon Blvd</strong> to see our full selection. We're conveniently located near <strong>North Park</strong>, <strong>City Heights</strong>, and <strong>Normal Heights</strong>. <a href="/contact">Get directions</a> or call <a href="tel:6196401633">(619) 640-1633</a>.</p>
      `
    },
    {
      id: 3,
      slug: 'promotional-products-san-diego-businesses',
      title: 'Promotional Products That Actually Work: A San Diego Business Guide',
      excerpt: 'From embroidered polos to custom pens, discover which promotional products deliver the best ROI for San Diego companies.',
      image: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/ehppwmya_unnamed%20%287%29.webp',
      date: 'June 20, 2026',
      author: 'RC Awards Team',
      readTime: '7 min read',
      category: 'Promotional Products',
      content: `
        <p><strong>Promotional products</strong> remain one of the most cost-effective marketing tools for <strong>San Diego businesses</strong>. Whether you're exhibiting at the <strong>San Diego Convention Center</strong>, sponsoring a <strong>Padres game</strong>, or hosting a company picnic at <strong>Balboa Park</strong>, the right promo items make lasting impressions.</p>

        <h3>Top Promotional Products for 2026</h3>

        <h4>1. Custom Embroidered Apparel</h4>
        <p><strong>Embroidered polos, hats, and jackets</strong> turn your employees into walking billboards. Our <a href="/services">embroidery services</a> can add your logo to virtually any garment. Popular with <strong>San Diego restaurants</strong>, <strong>real estate teams</strong>, and <strong>construction companies</strong>.</p>

        <h4>2. Branded Drinkware</h4>
        <p><strong>Custom water bottles and tumblers</strong> are perfect for health-conscious San Diego. Great for <strong>gyms</strong>, <strong>yoga studios</strong>, and <strong>outdoor companies</strong>. Your logo stays visible every time someone takes a sip.</p>

        <h4>3. Tech Accessories</h4>
        <p><strong>Power banks, phone stands, and webcam covers</strong> appeal to San Diego's thriving tech sector. Ideal giveaways for <strong>Sorrento Valley</strong> and <strong>UTC</strong> tech companies.</p>

        <h4>4. Eco-Friendly Products</h4>
        <p>San Diego loves sustainability! <strong>Recycled tote bags</strong>, <strong>bamboo pens</strong>, and <strong>seed paper</strong> show your commitment to the environment while promoting your brand.</p>

        <h4>5. Custom Name Badges</h4>
        <p>Professional <a href="/products">name badges</a> are essential for customer-facing businesses. We offer magnetic, pin-back, and clip options with full-color printing or elegant engraving.</p>

        <h3>Industry-Specific Recommendations</h3>
        
        <h4>Restaurants & Hospitality</h4>
        <p>Embroidered chef coats, custom aprons, branded to-go bags. Perfect for <strong>Gaslamp Quarter restaurants</strong> and <strong>Pacific Beach eateries</strong>.</p>

        <h4>Real Estate</h4>
        <p>Closing gifts, branded keychains, custom notepads. Make every <strong>La Jolla home sale</strong> or <strong>Coronado condo closing</strong> memorable.</p>

        <h4>Healthcare</h4>
        <p>Embroidered scrubs, stress balls, branded hand sanitizers. Ideal for <strong>San Diego medical practices</strong> and <strong>dental offices</strong>.</p>

        <h4>Trade Shows & Events</h4>
        <p>Pens, lanyards, tote bags, and snack items. Stock up before your next event at the <strong>Del Mar Fairgrounds</strong> or <strong>San Diego Convention Center</strong>.</p>

        <h3>Screen Printing vs. Embroidery</h3>
        <p>Not sure which method is right for your project?</p>
        <ul>
          <li><strong>Screen printing:</strong> Best for t-shirts, large orders, detailed multi-color designs</li>
          <li><strong>Embroidery:</strong> Best for polos, hats, jackets - adds a premium, professional look</li>
        </ul>
        <p>Our team can recommend the best approach. <a href="/contact">Request a consultation</a>.</p>

        <h3>Local Service, Competitive Prices</h3>
        <p>Why order online when you can work with a <strong>local San Diego promotional products company</strong>? At RC Awards, you get:</p>
        <ul>
          <li>In-person consultations at our <strong>El Cajon Blvd showroom</strong></li>
          <li>No minimum orders on many items</li>
          <li>Rush production when you need it fast</li>
          <li>30+ years of experience serving San Diego</li>
        </ul>

        <p><strong>Ready to promote your business?</strong> Browse our <a href="/products">product catalog</a>, check out our <a href="/gallery">work gallery</a>, or <a href="/contact">contact us</a> for a free quote. Call <a href="tel:6196401633">(619) 640-1633</a> today!</p>
      `
    },
    {
      id: 4,
      slug: 'trophies-san-diego-complete-buying-guide',
      title: 'Trophies San Diego: Complete Buying Guide for 2026',
      excerpt: 'Looking for trophies in San Diego? Compare trophy stores, prices, and turnaround times. Find the best trophy shop near you in San Diego County.',
      image: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/f08oactp_unnamed%20%288%29.webp',
      date: 'July 5, 2026',
      author: 'RC Awards Team',
      readTime: '8 min read',
      category: 'Trophies San Diego',
      content: `
        <p>Searching for <strong>trophies in San Diego</strong>? Whether you need sports trophies for a youth league, corporate awards for your business, or custom trophies for a special event, this guide covers everything you need to know about buying <strong>trophies in San Diego County</strong>.</p>

        <h3>Why Buy Trophies Locally in San Diego?</h3>
        <p>While online trophy shops exist, there are significant advantages to working with a <strong>local San Diego trophy store</strong>:</p>
        <ul>
          <li><strong>See samples in person</strong> before ordering</li>
          <li><strong>No shipping delays</strong> - pick up same day or next day</li>
          <li><strong>Support local business</strong> and the San Diego economy</li>
          <li><strong>Easy returns and adjustments</strong> if something isn't right</li>
          <li><strong>Personal service</strong> from experienced trophy professionals</li>
        </ul>

        <h3>Types of Trophies Available in San Diego</h3>
        
        <h4>Sports Trophies</h4>
        <p>From Little League baseball to adult softball leagues, <strong>San Diego sports trophies</strong> are our specialty. We carry:</p>
        <ul>
          <li>Baseball & softball trophies</li>
          <li>Soccer trophies and medals</li>
          <li>Basketball awards</li>
          <li>Football trophies</li>
          <li>Swimming and water polo awards</li>
          <li>Martial arts trophies</li>
          <li>Golf trophies and hole-in-one plaques</li>
        </ul>

        <h4>Corporate & Business Trophies</h4>
        <p><strong>San Diego businesses</strong> trust us for employee recognition awards, sales achievement trophies, and years of service awards. Popular with companies in <strong>Downtown San Diego</strong>, <strong>Sorrento Valley</strong>, <strong>UTC</strong>, and <strong>Mission Valley</strong>.</p>

        <h4>Academic Trophies</h4>
        <p>Schools throughout <strong>San Diego Unified</strong>, <strong>Grossmont Union</strong>, and <strong>Sweetwater Union</strong> districts order graduation awards, academic achievement trophies, and spelling bee prizes from us.</p>

        <h3>Trophy Pricing in San Diego</h3>
        <p>Trophy prices vary based on size, material, and customization:</p>
        <ul>
          <li><strong>Small participation trophies:</strong> $5-15 each</li>
          <li><strong>Medium achievement trophies:</strong> $15-35 each</li>
          <li><strong>Large championship trophies:</strong> $35-100+</li>
          <li><strong>Crystal and glass awards:</strong> $45-200+</li>
        </ul>
        <p>Bulk discounts available for orders of 25+ trophies. <a href="/contact">Contact us</a> for a custom quote.</p>

        <h3>San Diego Areas We Serve</h3>
        <p>Our <strong>trophy store on El Cajon Blvd</strong> serves all of San Diego County:</p>
        <ul>
          <li><strong>Central San Diego:</strong> Hillcrest, North Park, University Heights, Normal Heights</li>
          <li><strong>East County:</strong> La Mesa, El Cajon, Santee, Lakeside, Alpine</li>
          <li><strong>South Bay:</strong> Chula Vista, National City, Imperial Beach, Otay Mesa</li>
          <li><strong>North County:</strong> Escondido, Poway, Rancho Bernardo, San Marcos</li>
          <li><strong>Coastal:</strong> La Jolla, Pacific Beach, Ocean Beach, Point Loma, Coronado</li>
        </ul>

        <h3>Why Choose RC Awards for Trophies in San Diego?</h3>
        <p>Since 1995, <strong>RC Awards</strong> has been San Diego's trusted <strong>trophy store</strong>. Here's why customers choose us:</p>
        <ul>
          <li><strong>5.0 Google Rating</strong> with 200+ reviews</li>
          <li><strong>4-day standard turnaround</strong></li>
          <li><strong>Same-day rush service</strong> available</li>
          <li><strong>Best prices guaranteed</strong></li>
          <li><strong>Free local delivery</strong> in San Diego metro</li>
        </ul>

        <p><strong>Visit our showroom</strong> at 2509 El Cajon Blvd, San Diego, CA 92104 or call <a href="tel:6196401633">(619) 640-1633</a> for a free quote!</p>
      `
    },
    {
      id: 5,
      slug: 'same-day-engraving-san-diego',
      title: 'Same Day Engraving San Diego: Where to Get Last-Minute Engraving',
      excerpt: 'Need same day engraving in San Diego? Find out which shops offer rush engraving services, pricing, and how to get your item engraved today.',
      image: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/qafcxzfa_unnamed.webp',
      date: 'July 8, 2026',
      author: 'RC Awards Team',
      readTime: '6 min read',
      category: 'Engraving Services',
      content: `
        <p>Need <strong>same day engraving in San Diego</strong>? Whether it's a last-minute gift, a forgotten award, or an urgent business need, finding <strong>engraving near me</strong> that can do rush work isn't always easy. Here's your complete guide to <strong>same day engraving services in San Diego</strong>.</p>

        <h3>What Can Be Engraved Same Day?</h3>
        <p>At RC Awards, we offer <strong>same day engraving</strong> on many items:</p>
        <ul>
          <li><strong>Trophies and awards</strong> - Add names, dates, achievements</li>
          <li><strong>Plaques</strong> - Wood, acrylic, or metal</li>
          <li><strong>Name badges</strong> - For new employees or events</li>
          <li><strong>Pet tags</strong> - ID tags for dogs and cats</li>
          <li><strong>Jewelry</strong> - Bracelets, pendants, rings</li>
          <li><strong>Gifts</strong> - Picture frames, flasks, pens, watches</li>
          <li><strong>Tools</strong> - Knives, multi-tools, flashlights</li>
          <li><strong>Electronics</strong> - iPads, laptops, phones</li>
        </ul>

        <h3>Types of Engraving We Offer</h3>
        
        <h4>Laser Engraving</h4>
        <p><strong>Laser engraving in San Diego</strong> is our most popular service. Benefits include:</p>
        <ul>
          <li>Precise, detailed results</li>
          <li>Works on metal, wood, glass, acrylic, leather</li>
          <li>Photos and logos possible</li>
          <li>Fast turnaround</li>
        </ul>

        <h4>Rotary Engraving</h4>
        <p>Traditional <strong>rotary engraving</strong> creates deep cuts in metal. Best for:</p>
        <ul>
          <li>Brass and aluminum plates</li>
          <li>Name badges</li>
          <li>Industrial tags</li>
        </ul>

        <h4>Diamond Drag Engraving</h4>
        <p>Creates a polished, reflective finish on metal surfaces. Popular for:</p>
        <ul>
          <li>Jewelry</li>
          <li>Watches</li>
          <li>High-end gifts</li>
        </ul>

        <h3>Same Day Engraving Pricing</h3>
        <p><strong>Engraving prices in San Diego</strong> vary by item and complexity:</p>
        <ul>
          <li><strong>Simple text engraving:</strong> $5-15</li>
          <li><strong>Logo engraving:</strong> $15-30</li>
          <li><strong>Photo engraving:</strong> $25-50</li>
          <li><strong>Rush/same day fee:</strong> +$10-25</li>
        </ul>

        <h3>How to Get Same Day Engraving</h3>
        <ol>
          <li><strong>Call ahead:</strong> (619) 640-1633 to confirm availability</li>
          <li><strong>Bring your item</strong> to our shop on El Cajon Blvd</li>
          <li><strong>Provide text/artwork</strong> - email logos to RCAwards@Aol.com</li>
          <li><strong>Wait or return</strong> - most items done in 30-60 minutes</li>
        </ol>

        <h3>Engraving Near Me: San Diego Locations</h3>
        <p>Looking for <strong>engraving near me</strong> in San Diego? Our central location serves:</p>
        <ul>
          <li><strong>10 minutes from:</strong> Hillcrest, North Park, University Heights</li>
          <li><strong>15 minutes from:</strong> Mission Valley, La Mesa, Downtown</li>
          <li><strong>20 minutes from:</strong> Pacific Beach, La Jolla, El Cajon</li>
          <li><strong>30 minutes from:</strong> Chula Vista, Coronado, Poway</li>
        </ul>

        <h3>Why Choose RC Awards for Engraving?</h3>
        <ul>
          <li><strong>30+ years experience</strong> in San Diego</li>
          <li><strong>State-of-the-art equipment</strong></li>
          <li><strong>Same day service available</strong></li>
          <li><strong>Competitive pricing</strong></li>
          <li><strong>5-star Google reviews</strong></li>
        </ul>

        <p><strong>Need engraving today?</strong> Call <a href="tel:6196401633">(619) 640-1633</a> or visit us at 2509 El Cajon Blvd, San Diego. Walk-ins welcome!</p>
      `
    },
    {
      id: 6,
      slug: 'san-diego-personalized-awards-custom-recognition',
      title: 'San Diego Personalized Awards: Custom Recognition for Every Occasion',
      excerpt: 'Discover personalized awards in San Diego for employee recognition, sports teams, schools, and special events. Custom engraving, fast turnaround.',
      image: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/oumok1jw_unnamed%20%286%29.webp',
      date: 'July 10, 2026',
      author: 'RC Awards Team',
      readTime: '7 min read',
      category: 'Personalized Awards',
      content: `
        <p>Looking for <strong>personalized awards in San Diego</strong>? Whether you're recognizing top employees, celebrating athletic achievements, or honoring community leaders, custom awards make the moment memorable. Here's everything you need to know about <strong>San Diego personalized awards</strong>.</p>

        <h3>Types of Personalized Awards</h3>

        <h4>Employee Recognition Awards</h4>
        <p><strong>San Diego businesses</strong> use personalized awards for:</p>
        <ul>
          <li><strong>Years of Service:</strong> 5, 10, 15, 20+ year milestone awards</li>
          <li><strong>Employee of the Month/Year:</strong> Plaques, trophies, desk awards</li>
          <li><strong>Sales Achievement:</strong> Top performer recognition</li>
          <li><strong>Safety Awards:</strong> Accident-free workplace milestones</li>
          <li><strong>Retirement:</strong> Career celebration awards</li>
        </ul>

        <h4>Sports Awards</h4>
        <p>From youth leagues to adult recreational sports, we create <strong>personalized sports awards</strong> for:</p>
        <ul>
          <li>MVP and All-Star recognition</li>
          <li>Championship trophies</li>
          <li>Coach appreciation awards</li>
          <li>Team participation awards</li>
          <li>Tournament prizes</li>
        </ul>

        <h4>Academic Awards</h4>
        <p><strong>San Diego schools</strong> order personalized awards for:</p>
        <ul>
          <li>Honor roll recognition</li>
          <li>Graduation awards</li>
          <li>Perfect attendance</li>
          <li>Science fair winners</li>
          <li>Spelling bee champions</li>
        </ul>

        <h4>Special Event Awards</h4>
        <ul>
          <li><strong>Fundraiser recognition:</strong> Donor appreciation</li>
          <li><strong>Volunteer awards:</strong> Community service recognition</li>
          <li><strong>Competition prizes:</strong> Cook-offs, car shows, contests</li>
          <li><strong>Memorial awards:</strong> Scholarships and tributes</li>
        </ul>

        <h3>Personalization Options</h3>
        <p>Make your award unique with:</p>
        <ul>
          <li><strong>Custom text:</strong> Names, dates, achievements, quotes</li>
          <li><strong>Logo engraving:</strong> Company, team, or organization logos</li>
          <li><strong>Photo engraving:</strong> Laser-etched portraits or images</li>
          <li><strong>Color printing:</strong> Full-color plates and inserts</li>
          <li><strong>Custom shapes:</strong> Unique award designs</li>
        </ul>

        <h3>Popular Award Materials</h3>
        
        <h4>Crystal & Glass</h4>
        <p>Premium <strong>crystal awards</strong> are perfect for executive recognition and milestone celebrations. Our collection includes:</p>
        <ul>
          <li>3D laser-etched crystal</li>
          <li>Art glass sculptures</li>
          <li>Optical crystal towers</li>
        </ul>

        <h4>Wood & Bamboo</h4>
        <p>Warm, natural <strong>wood plaques</strong> and awards suit traditional settings. Options include walnut, cherry, oak, and eco-friendly bamboo.</p>

        <h4>Acrylic</h4>
        <p>Modern <strong>acrylic awards</strong> offer vibrant colors and unique shapes. Perfect for contemporary offices and creative industries.</p>

        <h4>Metal</h4>
        <p>Durable <strong>metal awards</strong> include aluminum, brass, and stainless steel options. Ideal for outdoor displays and long-lasting recognition.</p>

        <h3>Why San Diego Chooses RC Awards</h3>
        <p>For over 30 years, <strong>RC Awards</strong> has been San Diego's go-to source for <strong>personalized awards</strong>:</p>
        <ul>
          <li><strong>Huge selection:</strong> Thousands of award options in stock</li>
          <li><strong>Expert design help:</strong> Free layout and proofing</li>
          <li><strong>Fast turnaround:</strong> 4-day standard, same-day rush available</li>
          <li><strong>Best prices:</strong> Competitive pricing guaranteed</li>
          <li><strong>Local service:</strong> Visit our San Diego showroom</li>
        </ul>

        <h3>San Diego Organizations We Serve</h3>
        <ul>
          <li><strong>Corporations:</strong> Qualcomm, SAIC, General Atomics, Cubic</li>
          <li><strong>Healthcare:</strong> UC San Diego Health, Sharp, Scripps</li>
          <li><strong>Government:</strong> City of San Diego, County offices, military bases</li>
          <li><strong>Schools:</strong> SDUSD, private schools, colleges</li>
          <li><strong>Nonprofits:</strong> Chambers of commerce, charities, clubs</li>
        </ul>

        <p><strong>Ready to create personalized awards?</strong> Visit our showroom at 2509 El Cajon Blvd or call <a href="tel:6196401633">(619) 640-1633</a> for a free consultation!</p>
      `
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-[#1a365d] text-white pt-32 pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            RC Awards Blog
          </h1>
          <p className="text-xl text-white/80 max-w-2xl">
            Tips, trends, and insights on trophies, awards, promotional products, and recognition programs for San Diego businesses and organizations.
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid gap-10">
          {blogPosts.map((post, index) => (
            <article 
              key={post.id}
              data-testid={`blog-post-${post.id}`}
              className={`bg-white rounded-2xl shadow-lg overflow-hidden ${
                index === 0 ? 'lg:grid lg:grid-cols-2' : 'lg:grid lg:grid-cols-3'
              }`}
            >
              {/* Image */}
              <div className={`relative ${index === 0 ? 'lg:col-span-1' : 'lg:col-span-1'}`}>
                <img 
                  src={post.image}
                  alt={post.title}
                  className="w-full h-64 lg:h-full object-cover"
                  onError={(e) => {
                    e.target.src = `https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/f08oactp_unnamed%20%288%29.webp`;
                  }}
                />
                <span className="absolute top-4 left-4 bg-[#D4AF37] text-[#1a365d] text-xs font-bold px-3 py-1 rounded-full">
                  {post.category}
                </span>
              </div>

              {/* Content */}
              <div className={`p-8 ${index === 0 ? 'lg:col-span-1' : 'lg:col-span-2'}`}>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    {post.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <User size={14} />
                    {post.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {post.readTime}
                  </span>
                </div>

                <h2 className="text-2xl lg:text-3xl font-bold text-[#1a365d] mb-4 hover:text-[#D4AF37] transition-colors">
                  <Link to={`/blog/${post.slug}`}>
                    {post.title}
                  </Link>
                </h2>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {post.excerpt}
                </p>

                <Link 
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center gap-2 text-[#D4AF37] font-semibold hover:text-[#1a365d] transition-colors"
                >
                  Read Full Article
                  <ArrowRight size={18} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* SEO Keywords Section */}
      <section className="bg-[#1a365d] text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold mb-6">San Diego Areas We Serve</h2>
              <div className="flex flex-wrap gap-2">
                {[
                  'Downtown San Diego', 'La Jolla', 'Pacific Beach', 'Mission Beach',
                  'Ocean Beach', 'Point Loma', 'Coronado', 'Chula Vista', 'National City',
                  'El Cajon', 'La Mesa', 'Santee', 'Poway', 'Escondido', 'Oceanside',
                  'Carlsbad', 'Encinitas', 'Del Mar', 'Solana Beach', 'Mission Valley',
                  'Hillcrest', 'North Park', 'South Park', 'Golden Hill', 'Kensington',
                  'Normal Heights', 'City Heights', 'College Area', 'SDSU', 'UTC',
                  'Sorrento Valley', 'Mira Mesa', 'Scripps Ranch', 'Rancho Bernardo'
                ].map((area) => (
                  <span 
                    key={area}
                    className="bg-white/10 px-3 py-1 rounded-full text-sm"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-6">Our Products & Services</h2>
              <div className="flex flex-wrap gap-2">
                {[
                  'Trophies', 'Plaques', 'Crystal Awards', 'Medals', 'Ribbons',
                  'Name Badges', 'Custom Engraving', 'Laser Engraving', 'Embroidery',
                  'Screen Printing', 'Promotional Products', 'Corporate Awards',
                  'Sports Trophies', 'Academic Awards', 'Recognition Awards',
                  'Employee Awards', 'Sales Awards', 'Achievement Awards',
                  'Championship Trophies', 'Participation Trophies', 'Custom Gifts',
                  'Branded Apparel', 'Company Merchandise', 'Event Giveaways'
                ].map((item) => (
                  <Link 
                    key={item}
                    to={item.includes('Engraving') || item.includes('Embroidery') || item.includes('Printing') ? '/services' : '/products'}
                    className="bg-[#D4AF37]/20 hover:bg-[#D4AF37]/40 px-3 py-1 rounded-full text-sm transition-colors"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-[#1a365d] mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-gray-600 mb-8 text-lg">
            Visit our showroom or contact us for a free quote on trophies, awards, and promotional products.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="blog-cta-quote"
              className="bg-[#D4AF37] text-[#1a365d] px-8 py-3 rounded-full font-semibold hover:bg-[#c9a432] transition-colors"
            >
              Get Free Quote
            </Link>
            <a
              href="tel:6196401633"
              data-testid="blog-cta-call"
              className="bg-[#1a365d] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#2d4a7c] transition-colors flex items-center gap-2"
            >
              <Phone size={18} />
              (619) 640-1633
            </a>
            <a
              href="https://www.google.com/maps/place/RC+Awards/@32.7548,-117.13735,17z"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="blog-cta-visit"
              className="border-2 border-[#1a365d] text-[#1a365d] px-8 py-3 rounded-full font-semibold hover:bg-[#1a365d] hover:text-white transition-colors flex items-center gap-2"
            >
              <MapPin size={18} />
              Visit Showroom
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
